import { useId } from 'react';
import type { ReactNode } from 'react';
import { AlertCircle, Lightbulb } from 'lucide-react';

import { Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import type { BoDich } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import type { KetQuaDanhSach, LoiDong } from '../../nghiep-vu/danh-sach-ip';

/** Câu lỗi của một dòng. `max` là trần của cả danh sách (khác trần còn thêm được của blocklist). */
export function cauLoiDong(t: BoDich['t'], l: LoiDong, thamSo: { ipCuaBan?: string | null; max: number; ghiChuToiDa: number }): string {
  const chung = { dong: l.dong, giaTri: l.giaTri, cidr: l.cidr ?? l.giaTri };
  switch (l.ma) {
    case 'dinh_dang':
      return t('baoMat.dsIp.loiDinhDang', chung);
    case 'tien_to':
      return t('baoMat.dsIp.loiTienTo', chung);
    case 'trung':
      return t('baoMat.dsIp.loiTrung', { ...chung, dongTruoc: l.dongTruoc ?? 0 });
    case 'tat_ca':
      return t('baoMat.dsIp.loiTatCa', chung);
    case 'qua_so':
      return t('baoMat.dsIp.loiQuaSo', { dong: l.dong, max: thamSo.max });
    case 'ghi_chu_dai':
      return t('baoMat.dsIp.loiGhiChuDai', { dong: l.dong, max: thamSo.ghiChuToiDa });
    case 'chua_ip_cua_ban':
      return t('baoMat.dsIp.loiChuaIpCuaBan', { dong: l.dong, ip: thamSo.ipCuaBan ?? '' });
    case 'da_co':
      return t('baoMat.dsIp.loiDaCo', chung);
  }
}

/**
 * Ô nhập danh sách IP theo dòng, kiểu aaPanel (ADR-0098): mỗi dòng một IP hoặc CIDR, ghi chú sau `#`. Dùng chung cho
 * dải tin cậy (cả danh sách sửa tại chỗ) và ô thêm vào blocklist.
 *
 * Bố cục: ô nhập bên trái, hướng dẫn bên phải ở màn rộng; màn hẹp thì hướng dẫn xuống dưới ô. Lỗi từng dòng nằm ngay
 * dưới ô ("Dòng 3: …"), gợi ý không chặn (IP trần đuôi .0) nằm sau lỗi. Dòng đếm cho biết bao nhiêu dòng hợp lệ trên
 * trần của danh sách.
 *
 * Component chỉ hiển thị: việc đọc (`docDanhSachIp`) do nơi dùng làm, để nơi dùng quyết trần, IP của người dùng và
 * danh sách đã có.
 */
export function ONhapDanhSachIp({
  id,
  moTaId,
  giaTri,
  onDoi,
  chiDoc = false,
  kq,
  loiDong,
  max,
  viDu,
  huongDan,
  nhan,
  dauTren,
}: {
  id?: string;
  /** `aria-describedby` của `Field` bao ngoài (lỗi hoặc gợi ý chung). */
  moTaId?: string;
  giaTri: string;
  onDoi: (v: string) => void;
  chiDoc?: boolean;
  kq: KetQuaDanhSach;
  /** Câu lỗi đã dựng theo dòng, cả lỗi phía giao diện lẫn lỗi máy chủ trả về cho dòng. */
  loiDong: Array<{ dong: number; cau: string }>;
  /** Trần của danh sách, cho dòng đếm. */
  max: number;
  /** Các dòng ví dụ, hiện trong khung hướng dẫn. */
  viDu: string;
  /** Gạch đầu dòng hướng dẫn riêng của nơi dùng, thêm sau phần chung. */
  huongDan?: string[];
  /** Nhãn cho trình đọc màn hình khi ô không có `<label>` bên ngoài. */
  nhan?: string;
  /** Hàng trên ô nhập, ví dụ nút xuất danh sách. */
  dauTren?: ReactNode;
}) {
  const { t, locale } = useT();
  const idTuSinh = useId();
  const oId = id ?? idTuSinh;
  const loiId = `${oId}-loi-dong`;
  const huongDanId = `${oId}-huong-dan`;
  const demId = `${oId}-dem`;
  const coLoi = loiDong.length > 0;
  const describedBy = [moTaId, coLoi ? loiId : undefined, demId, huongDanId].filter(Boolean).join(' ');

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_17rem]">
      <div className="min-w-0 space-y-2">
        {dauTren}
        <Textarea
          id={oId}
          aria-label={nhan}
          aria-invalid={coLoi}
          aria-describedby={describedBy}
          className="font-mono text-sm"
          spellCheck={false}
          autoComplete="off"
          autoSize={{ minRows: 6, maxRows: 18 }}
          value={giaTri}
          disabled={chiDoc}
          status={coLoi ? 'error' : undefined}
          onChange={(e) => onDoi(e.target.value)}
        />
        {(coLoi || kq.goiY.length > 0) && (
          <ul id={loiId} className="space-y-1" aria-live="polite">
            {loiDong.map((l) => (
              <li key={`loi-${l.dong}`} className="flex items-start gap-1.5 text-xs text-danger-600">
                <AlertCircle size={13} className="mt-0.5 shrink-0" aria-hidden />
                <span className="min-w-0 break-words">{l.cau}</span>
              </li>
            ))}
            {kq.goiY.map((g) => (
              <li key={`goi-y-${g.dong}`} className="flex items-start gap-1.5 text-xs text-content-muted">
                <Lightbulb size={13} className="mt-0.5 shrink-0 text-warning-600" aria-hidden />
                <span className="min-w-0 break-words">{t('baoMat.dsIp.goiYDai', { dong: g.dong, ip: g.ip, cidr: g.cidr })}</span>
              </li>
            ))}
          </ul>
        )}
        <p id={demId} className="text-xs tabular-nums text-content-muted">
          {t('baoMat.dsIp.soDong', { so: soVN(kq.muc.length, locale), max: soVN(max, locale) })}
        </p>
      </div>

      <aside id={huongDanId} className="min-w-0 space-y-2 rounded border border-default bg-subtle p-3 text-xs leading-relaxed text-content-secondary">
        <p className="font-medium text-content-primary">{t('baoMat.dsIp.huongDanTieuDe')}</p>
        <ul className="list-disc space-y-1 pl-4">
          <li>{t('baoMat.dsIp.huongDanMotDong')}</li>
          <li>{t('baoMat.dsIp.huongDanGhiChu')}</li>
          <li>{t('baoMat.dsIp.huongDanMotDiaChi')}</li>
          {(huongDan ?? []).map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ul>
        <p className="font-medium text-content-primary">{t('baoMat.dsIp.viDuTieuDe')}</p>
        <pre className="overflow-x-auto whitespace-pre rounded border border-default bg-surface px-2 py-1.5 font-mono text-2xs text-content-primary">
          {viDu}
        </pre>
      </aside>
    </div>
  );
}
